import {
    Package,
    Truck,
    BarChart3,
    DollarSign,
    CheckSquare,
    Wrench,
    ShoppingCart,
    FileText,
    Users,
    Mail,
    Globe,
    Database,
    Zap,
    LayoutGrid,
    Building2,
    Briefcase,
    Heart,
    GraduationCap,
    Utensils,
    Hammer,
} from "lucide-react";

const KEYWORD_MAP = [
    { keywords: ["catalog", "product", "inventory", "wholesale", "retail"], icon: Package, theme: "emerald" },
    { keywords: ["transport", "logistics", "shipping", "delivery", "fleet"], icon: Truck, theme: "amber" },
    { keywords: ["dashboard", "analytics", "report", "monitor", "data"], icon: BarChart3, theme: "violet" },
    { keywords: ["finance", "billing", "invoice", "payment", "revenue", "accounting"], icon: DollarSign, theme: "green" },
    { keywords: ["task", "todo", "tracker", "project", "manage"], icon: CheckSquare, theme: "blue" },
    { keywords: ["workshop", "automotive", "repair", "mechanic", "service"], icon: Wrench, theme: "orange" },
    { keywords: ["sales", "crm", "lead", "pipeline", "deal"], icon: Briefcase, theme: "indigo" },
    { keywords: ["ecommerce", "shop", "store", "order", "cart"], icon: ShoppingCart, theme: "pink" },
    { keywords: ["document", "form", "contract", "template", "pdf"], icon: FileText, theme: "slate" },
    { keywords: ["team", "hr", "employee", "people", "staff"], icon: Users, theme: "cyan" },
    { keywords: ["email", "notification", "message", "communication"], icon: Mail, theme: "rose" },
    { keywords: ["website", "portal", "landing", "web"], icon: Globe, theme: "teal" },
    { keywords: ["database", "sql", "data", "manufacturing"], icon: Database, theme: "purple" },
    { keywords: ["health", "medical", "patient", "clinic"], icon: Heart, theme: "red" },
    { keywords: ["education", "course", "training", "learn"], icon: GraduationCap, theme: "yellow" },
    { keywords: ["restaurant", "food", "menu", "kitchen"], icon: Utensils, theme: "warmGray" },
    { keywords: ["construction", "build", "property", "real estate"], icon: Hammer, theme: "stone" },
    { keywords: ["licitac", "bid", "government", "tender", "procurement"], icon: Building2, theme: "sky" },
];

// Color palettes per theme - gradient, icon color, badge bg, accent border
const THEME_COLORS = {
    emerald:  { gradient: "from-emerald-50 to-teal-100", icon: "text-emerald-400", badge: "bg-emerald-100 text-emerald-700 border-emerald-200", accent: "hover:border-emerald-300" },
    amber:    { gradient: "from-amber-50 to-orange-100", icon: "text-amber-400", badge: "bg-amber-100 text-amber-700 border-amber-200", accent: "hover:border-amber-300" },
    violet:   { gradient: "from-violet-50 to-purple-100", icon: "text-violet-400", badge: "bg-violet-100 text-violet-700 border-violet-200", accent: "hover:border-violet-300" },
    green:    { gradient: "from-green-50 to-emerald-100", icon: "text-green-400", badge: "bg-green-100 text-green-700 border-green-200", accent: "hover:border-green-300" },
    blue:     { gradient: "from-blue-50 to-indigo-100", icon: "text-blue-400", badge: "bg-blue-100 text-blue-700 border-blue-200", accent: "hover:border-blue-300" },
    orange:   { gradient: "from-orange-50 to-amber-100", icon: "text-orange-400", badge: "bg-orange-100 text-orange-700 border-orange-200", accent: "hover:border-orange-300" },
    indigo:   { gradient: "from-indigo-50 to-blue-100", icon: "text-indigo-400", badge: "bg-indigo-100 text-indigo-700 border-indigo-200", accent: "hover:border-indigo-300" },
    pink:     { gradient: "from-pink-50 to-rose-100", icon: "text-pink-400", badge: "bg-pink-100 text-pink-700 border-pink-200", accent: "hover:border-pink-300" },
    slate:    { gradient: "from-slate-50 to-gray-100", icon: "text-slate-400", badge: "bg-slate-100 text-slate-700 border-slate-200", accent: "hover:border-slate-300" },
    cyan:     { gradient: "from-cyan-50 to-sky-100", icon: "text-cyan-400", badge: "bg-cyan-100 text-cyan-700 border-cyan-200", accent: "hover:border-cyan-300" },
    rose:     { gradient: "from-rose-50 to-pink-100", icon: "text-rose-400", badge: "bg-rose-100 text-rose-700 border-rose-200", accent: "hover:border-rose-300" },
    teal:     { gradient: "from-teal-50 to-cyan-100", icon: "text-teal-400", badge: "bg-teal-100 text-teal-700 border-teal-200", accent: "hover:border-teal-300" },
    purple:   { gradient: "from-purple-50 to-violet-100", icon: "text-purple-400", badge: "bg-purple-100 text-purple-700 border-purple-200", accent: "hover:border-purple-300" },
    red:      { gradient: "from-red-50 to-rose-100", icon: "text-red-400", badge: "bg-red-100 text-red-700 border-red-200", accent: "hover:border-red-300" },
    yellow:   { gradient: "from-yellow-50 to-amber-100", icon: "text-yellow-500", badge: "bg-yellow-100 text-yellow-700 border-yellow-200", accent: "hover:border-yellow-300" },
    warmGray: { gradient: "from-stone-50 to-orange-50", icon: "text-stone-400", badge: "bg-stone-100 text-stone-700 border-stone-200", accent: "hover:border-stone-300" },
    stone:    { gradient: "from-stone-50 to-slate-100", icon: "text-stone-400", badge: "bg-stone-100 text-stone-700 border-stone-200", accent: "hover:border-stone-300" },
    sky:      { gradient: "from-sky-50 to-blue-100", icon: "text-sky-400", badge: "bg-sky-100 text-sky-700 border-sky-200", accent: "hover:border-sky-300" },
};

const DEFAULT_THEME = { gradient: "from-gray-50 to-slate-100", icon: "text-gray-400", badge: "bg-gray-100 text-gray-700 border-gray-200", accent: "hover:border-gray-300" };

/**
 * Returns { Icon, colors } for a blueprint based on name/description keywords
 */
export function getBlueprintTheme(blueprint) {
    const text = `${blueprint.name || ""} ${blueprint.description || ""}`.toLowerCase();

    for (const entry of KEYWORD_MAP) {
        if (entry.keywords.some((kw) => text.includes(kw))) {
            return {
                Icon: entry.icon,
                colors: THEME_COLORS[entry.theme] || DEFAULT_THEME,
            };
        }
    }

    // Fallback based on category
    if (blueprint.category === "APP_V2") {
        return { Icon: LayoutGrid, colors: THEME_COLORS.violet };
    }
    return { Icon: Zap, colors: THEME_COLORS.blue };
}
